import React, { useState, useEffect, useCallback } from 'react';
import CandidateSubmissionForm from '../components/CandidateSubmissionForm';
import { 
  Users, 
  Filter, 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  MoreHorizontal,
  Calendar,
  Clock,
  User,
  Building,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

const CandidateSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecruiter, setSelectedRecruiter] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [availableStatuses, setAvailableStatuses] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // Fetch available statuses on component mount
  useEffect(() => {
    fetchAvailableStatuses();
    fetchAnalytics();
  }, []);

  // Fetch submissions when filters change
  useEffect(() => {
    fetchSubmissions();
  }, [selectedStatus, searchTerm, selectedRecruiter, selectedClient, currentPage]);

  const fetchAvailableStatuses = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/statuses/available`);
      if (response.ok) {
        const data = await response.json();
        setAvailableStatuses(data.statuses);
      }
    } catch (error) {
      console.error('Error fetching available statuses:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/analytics`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: '20'
      });

      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedRecruiter) params.append('recruiter_id', selectedRecruiter);
      if (selectedClient) params.append('client_name', selectedClient);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
        setTotalPages(data.pagination?.total_pages || 1);
      } else {
        console.error('Failed to fetch submissions');
        setSubmissions([]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (submissionId, newStatus, reason = '') => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submissionId}/status?new_status=${newStatus}&change_reason=${reason}&changed_by=recruiter&changed_by_name=Current User`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        fetchSubmissions(); // Refresh the list
        if (selectedSubmission?.id === submissionId) {
          fetchSubmissionDetails(submissionId); // Refresh modal if open
        }
      }
    } catch (error) {
      console.error('Error changing status:', error);
    }
  };

  const fetchSubmissionDetails = async (submissionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submissionId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedSubmission(data.submission);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'sourced': 'bg-blue-100 text-blue-800',
      'screened': 'bg-purple-100 text-purple-800',
      'internally_submitted': 'bg-yellow-100 text-yellow-800',
      'technical_screened': 'bg-indigo-100 text-indigo-800',
      'client_accepted': 'bg-green-100 text-green-800',
      'client_reviewing': 'bg-orange-100 text-orange-800',
      'client_waiting': 'bg-gray-100 text-gray-800',
      'client_rejected': 'bg-red-100 text-red-800',
      'sendout_created': 'bg-teal-100 text-teal-800',
      'interview_scheduled': 'bg-blue-100 text-blue-800',
      'interview_scheduled': 'bg-purple-100 text-purple-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_scheduled': 'bg-pink-100 text-pink-800',
      'offer_made': 'bg-emerald-100 text-emerald-800',
      'offer_accepted': 'bg-green-100 text-green-800',
      'offer_rejected': 'bg-red-100 text-red-800',
      'internally_rejected': 'bg-red-100 text-red-800',
      'candidate_not_interested': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status.includes('accepted') || status.includes('offer_accepted')) {
      return <CheckCircle className="w-4 h-4" />;
    } else if (status.includes('rejected') || status.includes('not_interested')) {
      return <XCircle className="w-4 h-4" />;
    } else if (status.includes('interview')) {
      return <Users className="w-4 h-4" />;
    } else {
      return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
      {getStatusIcon(status)}
      {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );

  const AnalyticsCard = ({ title, value, subtitle, icon: Icon, trend }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className="p-2 bg-blue-50 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center text-xs">
          <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
          <span className="text-green-500">{trend}</span>
        </div>
      )}
    </div>
  );

  const FilterPanel = () => (
    <div className={`bg-white border-t border-gray-200 p-4 ${showFilters ? 'block' : 'hidden'}`}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(availableStatuses).map(([category, statuses]) =>
              statuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))
            )}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Recruiter</label>
          <input
            type="text"
            value={selectedRecruiter}
            onChange={(e) => setSelectedRecruiter(e.target.value)}
            placeholder="Filter by recruiter"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
          <input
            type="text"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            placeholder="Filter by client"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="flex items-end">
          <button
            onClick={() => {
              setSelectedStatus('');
              setSelectedRecruiter('');
              setSelectedClient('');
              setSearchTerm('');
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );

  const SubmissionCard = ({ submission }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-gray-900">{submission.candidate_name || `Candidate ID: ${submission.candidate_id}`}</h3>
            <StatusBadge status={submission.current_status} />
          </div>
          
          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              <span>{submission.job_title}</span>
              {submission.client_name && <span>• {submission.client_name}</span>}
            </div>
            
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>Recruiter: {submission.recruiter_name}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>Submitted: {formatDate(submission.submission_date)}</span>
            </div>
            
            {submission.match_score > 0 && (
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span>Match Score: {(submission.match_score * 100).toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedSubmission(submission);
              fetchSubmissionDetails(submission.id);
              setShowSubmissionModal(true);
            }}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          
          <div className="relative group">
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
            
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => handleStatusChange(submission.id, 'client_reviewing', 'Moving to client review')}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Send to Client
              </button>
              <button
                onClick={() => handleStatusChange(submission.id, 'interview_scheduled', 'Scheduling interview')}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Schedule Interview
              </button>
              <button
                onClick={() => handleStatusChange(submission.id, 'offer_made', 'Extending job offer')}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Extend Offer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SubmissionModal = () => {
    if (!selectedSubmission) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Submission Details
              </h2>
              <button
                onClick={() => setShowSubmissionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Candidate Information</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">Name:</span> {selectedSubmission.candidate_name || 'Unknown'}</p>
                  <p><span className="font-medium">Job:</span> {selectedSubmission.job_title}</p>
                  <p><span className="font-medium">Client:</span> {selectedSubmission.client_name || 'Not specified'}</p>
                  <p><span className="font-medium">Recruiter:</span> {selectedSubmission.recruiter_name}</p>
                  <p><span className="font-medium">Priority:</span> {selectedSubmission.priority_level}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Status Information</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium">Current Status:</span>
                    <div className="mt-1">
                      <StatusBadge status={selectedSubmission.current_status} />
                    </div>
                  </div>
                  <p className="text-sm"><span className="font-medium">Submitted:</span> {formatDate(selectedSubmission.submission_date)}</p>
                  <p className="text-sm"><span className="font-medium">Updated:</span> {formatDate(selectedSubmission.updated_at)}</p>
                  {selectedSubmission.match_score > 0 && (
                    <p className="text-sm"><span className="font-medium">Match Score:</span> {(selectedSubmission.match_score * 100).toFixed(1)}%</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Status History */}
            {selectedSubmission.status_history && selectedSubmission.status_history.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Status History</h3>
                <div className="space-y-3">
                  {selectedSubmission.status_history.map((entry, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <StatusBadge status={entry.status} />
                      </div>
                      <div className="flex-1 text-sm">
                        <p className="font-medium">{entry.reason}</p>
                        {entry.notes && <p className="text-gray-600 mt-1">{entry.notes}</p>}
                        <p className="text-gray-500 mt-1">
                          {entry.changed_by_name} • {formatDate(entry.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Notes */}
            {selectedSubmission.internal_notes && selectedSubmission.internal_notes.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Internal Notes</h3>
                <div className="space-y-3">
                  {selectedSubmission.internal_notes.map((note, index) => (
                    <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm">{note.note}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {note.author} • {formatDate(note.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Candidate Submissions</h1>
              <p className="text-gray-600 mt-1">Track and manage candidate submissions through the hiring pipeline</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  showFilters 
                    ? 'bg-blue-50 border-blue-200 text-blue-700' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              
              <button 
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Submission
              </button>
            </div>
          </div>
        </div>
        
        <FilterPanel />
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <AnalyticsCard
              title="Total Submissions"
              value={analytics.total_submissions || 0}
              icon={Users}
            />
            <AnalyticsCard
              title="Active Submissions"
              value={analytics.active_submissions || 0}
              icon={Clock}
            />
            <AnalyticsCard
              title="Offer Acceptance Rate"
              value={`${(analytics.offer_acceptance_rate * 100 || 0).toFixed(1)}%`}
              icon={CheckCircle}
            />
            <AnalyticsCard
              title="Client Acceptance Rate"
              value={`${(analytics.client_acceptance_rate * 100 || 0).toFixed(1)}%`}
              icon={TrendingUp}
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading submissions...</p>
            </div>
          </div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions found</h3>
            <p className="text-gray-600">Try adjusting your filters or create a new submission.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <SubmissionCard key={submission.id} submission={submission} />
            ))}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showSubmissionModal && <SubmissionModal />}
      <CandidateSubmissionForm
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSuccess={() => {
          fetchSubmissions(); // Refresh the list
        }}
      />
    </div>
  );
};

export default CandidateSubmissions;